import { Link } from '@tanstack/react-router';
import { ArrowRight, Check } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';
import type { OperationsDayResponse } from '@/shared/api';
import { can, canAccess, ROLE_GROUPS, useAuth } from '@/shared/auth';
import { CLEANING_QUEUE_SURFACE } from '@/shared/auth/privilegeSurfaces';
import { heuteTodos, type HeuteTodo } from './heuteTodoModel';

const cardClass =
  'border-hairline shadow-float flex min-h-[156px] flex-col gap-2 rounded-tile border bg-white p-4 text-left text-ink no-underline';

function TodoBody({ todo, index }: { todo: HeuteTodo; index: number }) {
  return (
    <>
      <span className="flex items-center gap-2">
        <span
          className={cn(
            'rounded-pill flex size-7 items-center justify-center text-xs font-extrabold',
            todo.hot ? 'bg-sealwax text-white' : 'bg-card-alt text-ink',
          )}
        >
          {index + 1}
        </span>
        <span className="text-ink-soft font-mono text-[10px] font-semibold uppercase tracking-[0.14em]">
          {todo.tag}
        </span>
        {todo.count > 1 && (
          <span className="text-ink-soft ml-auto text-xs font-bold">{todo.count}×</span>
        )}
      </span>
      <span className="font-serif text-lg font-bold leading-tight">{todo.title}</span>
      <span className="text-ink-soft line-clamp-2 text-[13px]">{todo.detail}</span>
      {todo.action.kind !== 'none' && (
        <span className="text-forest mt-auto flex items-center gap-1.5 text-[13px] font-bold">
          {todo.action.label}
          <ArrowRight size={14} aria-hidden />
        </span>
      )}
    </>
  );
}

/**
 * Nummerierte Aufgaben aus den Hinweisen des Servers, als Karten, die in den Kopf ragen. Was sich
 * auf ein Apartment bezieht, öffnet dessen Tagesdetails; der Rest führt auf die passende Seite.
 */
export function HeuteTodos({
  data,
  future,
  onOpenApartment,
}: {
  data: OperationsDayResponse;
  /** Gefährdete Anreisen und fehlende Wechsel prüft der Server erst am Tag selbst. */
  future: boolean;
  onOpenApartment: (propertyId: string) => void;
}) {
  const { user } = useAuth();
  const todos = heuteTodos(data, {
    staff: canAccess(user, ROLE_GROUPS.staff),
    cleaning: can(user, CLEANING_QUEUE_SURFACE.privilege),
    bookings: canAccess(user, ROLE_GROUPS.operationsOwner),
  });
  const futureNote = future
    ? 'Gefährdete Anreisen und fehlende Wechsel-Reinigungen prüft der Server erst am Tag selbst.'
    : null;
  if (todos.length === 0) {
    return (
      <Shell>
        <div className={cn(cardClass, 'min-h-0 flex-row items-center gap-3')}>
          <span className="bg-forest-soft text-forest rounded-pill flex size-9 items-center justify-center">
            <Check size={18} aria-hidden />
          </span>
          <span className="flex flex-col">
            <span className="font-serif text-lg font-bold">
              {future ? 'Bisher nichts offen' : 'Nichts offen'}
            </span>
            <span className="text-ink-soft text-[13px]">
              Für diesen Tag meldet der Server keine Aufgabe.{futureNote ? ` ${futureNote}` : ''}
            </span>
          </span>
        </div>
      </Shell>
    );
  }
  return (
    <Shell>
      <ol className="grid grid-cols-[repeat(auto-fill,minmax(min(240px,100%),1fr))] gap-3.5">
        {todos.map((todo, index) => (
          <li key={todo.code} className="flex">
            <TodoCard todo={todo} index={index} onOpenApartment={onOpenApartment} />
          </li>
        ))}
      </ol>
      {futureNote && <p className="text-ink-soft mt-2 text-xs">{futureNote}</p>}
    </Shell>
  );
}

function TodoCard({
  todo,
  index,
  onOpenApartment,
}: {
  todo: HeuteTodo;
  index: number;
  onOpenApartment: (propertyId: string) => void;
}) {
  const { action } = todo;
  if (action.kind === 'apartment') {
    return (
      <button
        type="button"
        onClick={() => onOpenApartment(action.propertyId)}
        className={cn(cardClass, 'w-full')}
      >
        <TodoBody todo={todo} index={index} />
      </button>
    );
  }
  if (action.kind === 'none') {
    return (
      <div className={cn(cardClass, 'w-full')}>
        <TodoBody todo={todo} index={index} />
      </div>
    );
  }
  return (
    <Link to={action.to} search={action.search ?? {}} className={cn(cardClass, 'w-full')}>
      <TodoBody todo={todo} index={index} />
    </Link>
  );
}

function Shell({ children }: { children: ReactNode }) {
  return (
    <section aria-label="Aufgaben" className="relative">
      {children}
    </section>
  );
}
